import { useEffect, useState } from 'react';
import { Download, FileText, BookOpen } from 'lucide-react';
import { api } from '../../lib/api';
import type { StaffHandbook } from '../../types';
import { formatDate, formatFileSize } from '../../lib/format';
import { Spinner, EmptyState, Alert } from '../../components/ui';

export default function Handbook() {
  const [handbook, setHandbook] = useState<StaffHandbook | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .get<StaffHandbook | null>('/handbooks/latest')
      .then(setHandbook)
      .catch(() => setError('Failed to load the staff handbook. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner label="Loading staff handbook..." />;

  return (
    <div>
      <div className="flex items-center mb-2">
        <BookOpen className="h-6 w-6 text-gray-500 mr-2" />
        <h1 className="text-2xl font-bold">Staff Handbook</h1>
      </div>
      <p className="text-sm text-gray-500 mb-8">
        Company policies, procedures and guidelines for all staff.
      </p>

      {error && <Alert>{error}</Alert>}

      {!handbook ? (
        <EmptyState message="No staff handbook has been published yet. Check back soon." />
      ) : (
        <div className="bg-white rounded-lg shadow-lg overflow-hidden max-w-2xl">
          <div className="flex items-start space-x-4 p-6 border-b">
            <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <FileText className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="font-semibold text-lg">{handbook.title}</h2>
              <p className="text-sm text-gray-500 mt-1">
                {handbook.filename} · {formatFileSize(handbook.size)} · Updated {formatDate(handbook.created_at)}
              </p>
            </div>
          </div>
          <div className="p-6 bg-gray-50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-sm text-gray-500">
              Download the latest version of the staff handbook to review company policies and procedures.
            </p>
            <a
              href={handbook.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-5 py-2.5 bg-primary text-black rounded-lg text-sm font-semibold hover:bg-primary-dark"
            >
              <Download className="h-4 w-4 mr-2" /> Download
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
